import { useState } from 'react';
import type { ValveCard } from '@shared/domain';
import { useMakeup, useValveCommand, useValveMode } from '@/lib/query';
import { Badge, Card, SectionTitle, Toggle } from '@/components/ui';
import { PageBanner } from '@/components/layout/PageBanner';

function ValvePanel({ v }: { v: ValveCard }) {
  const mode = useValveMode();
  const cmd = useValveCommand();
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const setManual = (manual: boolean) => {
    setMsg(null);
    mode.mutate({ id: v.id, mode: manual ? 'manual' : 'auto' });
  };
  const toggle = (open: boolean) => {
    setMsg(null);
    cmd
      .mutateAsync({ id: v.id, open })
      .then((r) => setMsg({ kind: 'ok', text: `已下发并读回：open=${r.open}` }))
      .catch((e: Error) => setMsg({ kind: 'err', text: e.message }));
  };

  return (
    <Card className="p-5">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[15px] font-semibold text-slate-800">{v.title}</div>
          <div className="mt-1 text-[13px] text-slate-500">{v.subtitle}</div>
        </div>
        <Badge className={v.mode === 'manual' ? 'bg-warn-bg text-warn-fg border-warn-border' : undefined}>{v.mode === 'manual' ? '手动' : '自动'}</Badge>
      </div>

      {v.levelPct !== undefined ? (
        <div className="mt-3 text-[13px] text-slate-500">
          液位 {v.levelPct.toFixed(3)}% · ORP {v.orpMv?.toFixed(1)} mV · 分配：{v.allocation}
        </div>
      ) : null}

      <div className="mt-4 border-t border-hairline pt-3">
        <div className="text-[13px] font-medium text-slate-600">控制模式</div>
        <div className="mt-2 flex gap-2">
          <button onClick={() => setManual(false)} disabled={mode.isPending || v.mode === 'auto'} className={`btn ${v.mode === 'auto' ? 'btn-dark' : 'btn-ghost'} !py-1 !px-3 !text-[13px] disabled:opacity-60`}>自动</button>
          <button onClick={() => setManual(true)} disabled={mode.isPending || v.mode === 'manual'} className={`btn ${v.mode === 'manual' ? 'btn-dark' : 'btn-ghost'} !py-1 !px-3 !text-[13px] disabled:opacity-60`}>手动</button>
        </div>
        <p className="mt-2 text-[12px] text-slate-400">{v.note}</p>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <span className="text-[13px] text-slate-600">阀门</span>
        <Toggle checked={v.open} disabled={!v.manualAllowed || cmd.isPending} onChange={toggle} label={`${v.title} 开关`} />
        {cmd.isPending ? <span className="text-[12px] text-slate-400">下发中…</span> : null}
      </div>
      {msg ? (
        <div className={`mt-2 text-[12px] ${msg.kind === 'ok' ? 'text-ok-fg' : 'text-alarm-fg'}`}>{msg.text}</div>
      ) : null}
    </Card>
  );
}

export default function MakeupSource() {
  const { data: valves } = useMakeup();
  return (
    <div>
      <PageBanner />
      <div className="mb-6">
        <h2 className="text-[24px] font-bold text-slate-900">源水与补水系统</h2>
        <p className="mt-1 text-[14px] text-slate-500">反洗顺控 · 三池级联 · 前馈/低压熔断</p>
        <div className="mt-1 text-[13px] text-slate-400">接口数据 · 快照约每秒刷新</div>
      </div>

      <SectionTitle title="补水调度中枢" desc="分配量取自孪生水力学仿真；指令台账在读回成功或超时结案后解除等待状态。" />
      <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
        {valves?.map((v) => (
          <ValvePanel key={v.id} v={v} />
        ))}
      </div>
    </div>
  );
}
